import { LockClosedIcon } from '@heroicons/react/24/outline'
import { useFormik } from 'formik'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { QUERY_KEYS } from 'constants/constants'
import {
  useNotificationContext,
  usePartnerContext,
  usePaymentContext
} from '../../../context'
import { useFeatureGate } from '../../../features/pricing/use-feature-gate'
import { classNames } from '../../../helpers'
import { useMutationAndUpdateQuery } from '../../../hooks/use-mutation-and-update-query'
import Alert from '../../alert'
import SelectMenu from '../../select-menu'
import InfoCallout from '../../tailwind/InfoCallout'
import Toggle from '../../tailwind/toggle'
import SettingsContainer from '../settings-container'

const i18n_remind_spaces = 'settings.automations.remind_spaces.'

const AUTOMATION_FEATURE = 'GDPR_AUTOMATION'
const AUTOMATION_SOURCE = 'settings_automations_remind'

/**
 * Was ein Klick auf den Schalter tut. In legacy und preview wie bisher, ohne
 * das Produkt öffnet jeder Klick die Paywall. In enforced geht Ausschalten
 * immer. Ist die Erinnerung gespeichert an (in Nutzung), lässt sie sich also
 * aus- und wieder auf den gespeicherten Stand einschalten. Neu einschalten
 * öffnet den Upgrade-Dialog.
 *
 * @returns {'toggle'|'paywall'|'dialog'}
 */
export const remindToggleAction = ({ enforced, available, inUse }) => {
  if (available) return 'toggle'
  if (!enforced) return 'paywall'
  return inUse ? 'toggle' : 'dialog'
}

const RemindSpaces = () => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { success } = useNotificationContext()
  const { automations: automationsProductAvailable, showPaywall } =
    usePaymentContext()

  const [conflict, setConflict] = useState(null)

  const automation_assessment_reminder_active = useMemo(() => {
    const { automation_assessment_reminder_active } = JSON.parse(
      partner.app_settings?.automations || '{}'
    )

    return !!automation_assessment_reminder_active
  }, [partner])

  // Neue Lock-UX nur in enforced, in legacy und preview bleibt die Paywall
  const gate = useFeatureGate(AUTOMATION_FEATURE, {
    source: AUTOMATION_SOURCE,
    inUse: automation_assessment_reminder_active
  })

  const automation_assessment_reminder_days = useMemo(() => {
    const { automation_assessment_reminder_days } = JSON.parse(
      partner.app_settings?.automations || '{}'
    )

    return automation_assessment_reminder_days || 7
  }, [partner])

  const partnerMutation = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.partnerById(partner.id),
    mutation: 'updatePartner'
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      active: automation_assessment_reminder_active,
      days: automation_assessment_reminder_days
    },
    validationSchema: Yup.object({
      remind_spaces: Yup.bool(),
      days: Yup.number()
    }),
    validate: ({ active: aar_active, days: aar_days }) => {
      const app_settings = partner.app_settings || {}
      const aea_days = app_settings.assessment_expires_after_days || 14

      if (aar_active && automationsProductAvailable && aar_days >= aea_days) {
        setConflict({
          title: t(i18n_remind_spaces + 'conflict_title'),
          description: t(i18n_remind_spaces + 'conflict_description')
        })
      } else setConflict(null)

      return {}
    },
    onSubmit: ({ active: aar_active, days: aar_days }, { setSubmitting }) => {
      setSubmitting(true)

      const app_settings = partner.app_settings || {}
      const automations = JSON.parse(app_settings.automations || '{}')

      automations.automation_assessment_reminder_active = aar_active
      automations.automation_assessment_reminder_days = aar_days

      const input = { id: partner.id }
      input.app_settings = {
        ...app_settings,
        automations: JSON.stringify(automations)
      }

      const onSuccess = () => {
        setSubmitting(false)
        success(t('settings.automations.success_message'))
      }

      partnerMutation.mutate(
        { input },
        { onSuccess, onError: () => setSubmitting(false) }
      )
    }
  })

  const set_days_available = automationsProductAvailable && formik.values.active
  // In enforced und in Nutzung bleiben die Tage lesbar, ändern lassen sie sich
  // aber nicht
  const days_read_only = gate.readOnly && formik.values.active

  return (
    <SettingsContainer
      title={t(i18n_remind_spaces + 'settings_container_title')}
      description={t(i18n_remind_spaces + 'settings_container_description')}
      onSubmit={formik.handleSubmit}
      dirty={conflict ? false : formik.dirty}
      isLoading={formik.isSubmitting}
    >
      <div className='space-y-5'>
        {gate.readOnly && (
          <InfoCallout tone='neutral' icon={LockClosedIcon}>
            {t('pricing.gates.automation_off_hint')}
          </InfoCallout>
        )}
        <Toggle
          label={t(i18n_remind_spaces + 'toggle_label')}
          description={t(i18n_remind_spaces + 'toggle_description')}
          locked={!automationsProductAvailable}
          lockedFeature={gate.enforced ? AUTOMATION_FEATURE : undefined}
          lockedSource={AUTOMATION_SOURCE}
          lockedInUse={gate.readOnly}
          enabled={formik.values.active}
          setEnabled={() => {
            const action = remindToggleAction({
              enforced: gate.enforced,
              available: automationsProductAvailable,
              inUse: gate.readOnly
            })
            if (action === 'paywall') showPaywall('general')
            else if (action === 'dialog') gate.open()
            else formik.setFieldValue('active', !formik.values.active)
          }}
          appearance='with-left-label-and-description'
        />
        <div
          className={classNames(
            set_days_available || days_read_only ? '' : 'opacity-40'
          )}
        >
          <SelectMenu
            id='days'
            // label={t(i18n_remind_spaces + 'days_title')}
            label={t(i18n_remind_spaces + 'days_description')}
            options={[
              ...Array.from({ length: 30 }, (_, index) => index + 1).map(
                (days) => [
                  days,
                  t(
                    `${i18n_remind_spaces}days_${days === 1 ? 'day' : 'days'}`,
                    { number: days }
                  )
                ]
              )
            ]}
            defaultValue={formik.values.days}
            onChange={(days) => formik.setFieldValue('days', days)}
            className='w-full sm:w-2/3'
            translate={false}
            disabled={!set_days_available}
          />
        </div>
        {!conflict && formik.dirty && (
          <div className='max-w-lg'>
            <Alert
              type='info'
              title={t('settings.automations.info_title')}
              description={t('settings.automations.info_description')}
            />
          </div>
        )}
        {conflict && (
          <div className='max-w-lg'>
            <Alert type='error' {...conflict} />
          </div>
        )}
      </div>
    </SettingsContainer>
  )
}

export default RemindSpaces
